import { useTranslation } from "react-i18next"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FormError } from "@/components/FormError"
import { formatCNPJ, formatPhone } from "@/lib/masks"
import { noAutofillProps } from "@/lib/form"

export interface CompanyFormData {
  name: string
  tax_name: string
  tax_id: string
  email: string
  phone: string
  street: string
  city: string
  state: string
}

export const emptyCompanyFormData: CompanyFormData = {
  name: "",
  tax_name: "",
  tax_id: "",
  email: "",
  phone: "",
  street: "",
  city: "",
  state: "",
}

export interface CompanyFormErrors {
  name?: string | string[]
  tax_name?: string | string[]
  tax_id?: string | string[]
  email?: string | string[]
  phone?: string | string[]
  street?: string | string[]
  city?: string | string[]
  state?: string | string[]
}

interface CompanyFormProps {
  data: CompanyFormData
  errors: CompanyFormErrors
  onChange: (field: keyof CompanyFormData, value: string) => void
  idPrefix: string
  translationPrefix: string
}

export function CompanyForm({ data, errors, onChange, idPrefix, translationPrefix }: CompanyFormProps) {
  const { t } = useTranslation()

  function handleCNPJChange(e: React.ChangeEvent<HTMLInputElement>) {
    const formatted = formatCNPJ(e.target.value)
    onChange("tax_id", formatted)
  }

  function handlePhoneChange(e: React.ChangeEvent<HTMLInputElement>) {
    const formatted = formatPhone(e.target.value)
    onChange("phone", formatted)
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor={`${idPrefix}-name`}>{t(`${translationPrefix}.nameLabel`)} *</Label>
        <Input
          id={`${idPrefix}-name`}
          value={data.name}
          onChange={(e) => onChange("name", e.target.value)}
          placeholder={t(`${translationPrefix}.namePlaceholder`)}
          required
          {...noAutofillProps}
        />
        <FormError error={errors.name} />
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor={`${idPrefix}-tax_name`}>{t(`${translationPrefix}.taxNameLabel`)}</Label>
        <Input
          id={`${idPrefix}-tax_name`}
          value={data.tax_name}
          onChange={(e) => onChange("tax_name", e.target.value)}
          placeholder={t(`${translationPrefix}.taxNamePlaceholder`)}
          {...noAutofillProps}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-tax_id`}>{t(`${translationPrefix}.taxIdLabel`)}</Label>
        <Input
          id={`${idPrefix}-tax_id`}
          value={data.tax_id}
          onChange={handleCNPJChange}
          placeholder="00.000.000/0000-00"
          {...noAutofillProps}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-email`}>{t("common.email")}</Label>
        <Input
          id={`${idPrefix}-email`}
          type="email"
          value={data.email}
          onChange={(e) => onChange("email", e.target.value)}
          placeholder={t(`${translationPrefix}.emailPlaceholder`)}
          {...noAutofillProps}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-phone`}>{t(`${translationPrefix}.phoneLabel`)}</Label>
        <Input
          id={`${idPrefix}-phone`}
          value={data.phone}
          onChange={handlePhoneChange}
          placeholder="(00) 00000-0000"
          {...noAutofillProps}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-street`}>{t(`${translationPrefix}.streetLabel`)}</Label>
        <Input
          id={`${idPrefix}-street`}
          value={data.street}
          onChange={(e) => onChange("street", e.target.value)}
          placeholder={t(`${translationPrefix}.streetPlaceholder`)}
          {...noAutofillProps}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-city`}>{t(`${translationPrefix}.cityLabel`)}</Label>
        <Input
          id={`${idPrefix}-city`}
          value={data.city}
          onChange={(e) => onChange("city", e.target.value)}
          placeholder={t(`${translationPrefix}.cityPlaceholder`)}
          {...noAutofillProps}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-state`}>{t(`${translationPrefix}.stateLabel`)}</Label>
        <Input
          id={`${idPrefix}-state`}
          value={data.state}
          onChange={(e) => onChange("state", e.target.value)}
          placeholder={t(`${translationPrefix}.statePlaceholder`)}
          {...noAutofillProps}
        />
      </div>
    </div>
  )
}
